import React, { useState, useEffect, useRef } from 'react';
import { Icons } from '../components/Icons';
import MiniCipher from '../components/MiniCipher';
import AddNodeModal from '../components/AddNodeModal';
import AddEdgeModal from '../components/AddEdgeModal';
import { db, appId } from '../utils/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

const RabbitHoleView = ({ dbData, isAdmin, showToast }) => {
    const [nodes, setNodes] = useState([]);
    const [edges, setEdges] = useState([]);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    
    const [isPanning, setIsPanning] = useState(false);
    const [draggingNode, setDraggingNode] = useState(null);
    const [lastPointer, setLastPointer] = useState(null); 
    
    const [showNodeModal, setShowNodeModal] = useState(false);
    const [showEdgeModal, setShowEdgeModal] = useState(false);
    
    const boardRef = useRef(null);

    const availableItems = [...(dbData?.artifacts || []), ...(dbData?.ideas || []), ...(dbData?.journals || [])].map(item => {
        let mType = 'Log';
        if (dbData?.artifacts?.find(a => a.id === item.id)) mType = 'Artifact';
        if (dbData?.ideas?.find(a => a.id === item.id)) mType = 'Lore';
        if (item.author) mType = item.author + ' Log'; 
        return { ...item, matrixType: mType };
    });

    useEffect(() => {
        const loadMatrix = async () => {
            try {
                const snap = await getDoc(doc(db, 'artifacts', appId, 'public', 'data', 'matrix', 'layout'));
                if (snap.exists()) {
                    setNodes(snap.data().nodes || []);
                    setEdges(snap.data().edges || []);
                }
            } catch (e) { console.error("Error loading matrix:", e); }
        };
        loadMatrix();
    }, []);

    const saveMatrixState = async (newNodes, newEdges) => {
        if (!isAdmin) return;
        try { await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'matrix', 'layout'), { nodes: newNodes, edges: newEdges }); } 
        catch (e) { console.error("Save failed", e); }
    };

    const handlePointerDown = (e, nodeId) => {
        if (nodeId && isAdmin) {
            e.stopPropagation(); setDraggingNode(nodeId); e.target.setPointerCapture(e.pointerId);
        } else { setIsPanning(true); }
        setLastPointer({ x: e.clientX, y: e.clientY });
    };

    const handlePointerMove = (e) => {
        if (!lastPointer) return;
        const dx = e.clientX - lastPointer.x; const dy = e.clientY - lastPointer.y;
        if (isPanning) setPan(prev => ({ x: prev.x + dx, y: prev.y + dy }));
        else if (draggingNode !== null) setNodes(prev => prev.map(n => n.id === draggingNode ? { ...n, x: n.x + dx / zoom, y: n.y + dy / zoom } : n));
        setLastPointer({ x: e.clientX, y: e.clientY });
    };

    const handlePointerUp = (e) => {
        if (draggingNode) { saveMatrixState(nodes, edges); e.target.releasePointerCapture(e.pointerId); }
        setIsPanning(false); setDraggingNode(null); setLastPointer(null);
    };

    const addNode = (item, type) => {
        if (nodes.find(n => n.dataId === item.id)) return showToast("Already on board.", "error");
        let spawnX = 0; let spawnY = 0;
        if (boardRef.current) {
            const rect = boardRef.current.getBoundingClientRect();
            spawnX = ((rect.width / 2) - pan.x) / zoom; spawnY = ((rect.height / 2) - pan.y) / zoom;
        }
        const newNode = { id: 'node_' + Date.now(), dataId: item.id, label: item.title || item.name, type: type, x: spawnX, y: spawnY };
        const updatedNodes = [...nodes, newNode];
        setNodes(updatedNodes); setShowNodeModal(false); saveMatrixState(updatedNodes, edges);
    };

    const addEdge = (sourceId, targetId, cipherRecord, track, transitLog) => {
        const newEdge = {
            id: 'edge_' + Date.now(), source: sourceId, target: targetId,
            cipherType: cipherRecord.type, cipherCode: cipherRecord.code, cipherVisual: cipherRecord.visualData,
            track: track, transitLog: transitLog
        };
        const updatedEdges = [...edges, newEdge];
        setEdges(updatedEdges); setShowEdgeModal(false); saveMatrixState(nodes, updatedEdges);
    };

    const deleteNode = (id) => {
        const updatedNodes = nodes.filter(n => n.id !== id);
        const updatedEdges = edges.filter(e => e.source !== id && e.target !== id);
        setNodes(updatedNodes); setEdges(updatedEdges); saveMatrixState(updatedNodes, updatedEdges);
    };

    const deleteEdge = (id) => {
        const updatedEdges = edges.filter(e => e.id !== id);
        setEdges(updatedEdges); saveMatrixState(nodes, updatedEdges);
    };

    return (
        <div style={{ height: '100%', width: '100%', position: 'relative', backgroundColor: '#050505', overflow: 'hidden' }} className="fade-in">
            <div className="absolute top-4 left-4 right-4 z-[500] flex justify-between items-start pointer-events-none">
                {isAdmin && (
                    <div className="bg-black/90 backdrop-blur border border-gray-800 p-2 rounded pointer-events-auto flex gap-2 shadow-2xl">
                        <button onClick={() => setShowNodeModal(true)} className="bg-gray-900 hover:bg-gray-800 text-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest transition-colors flex items-center gap-2 rounded border border-gray-700"><Icons.Plus size={12} /> Add Node</button>
                        <button onClick={() => setShowEdgeModal(true)} className="bg-blue-900/20 text-blue-400 hover:bg-blue-900 hover:text-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest transition-colors flex items-center gap-2 rounded border border-blue-900/50"><Icons.Network size={12} /> Add Vector</button>
                    </div>
                )}
                <div className="bg-black/90 backdrop-blur border border-gray-800 p-2 rounded pointer-events-auto flex gap-2 shadow-2xl text-white font-bold items-center ml-auto">
                    <button onClick={() => setZoom(z => Math.max(0.2, z - 0.1))} className="px-2 hover:text-cyan-400 transition-colors">-</button>
                    <span className="text-[10px] font-mono w-10 text-center tracking-widest">{Math.round(zoom * 100)}%</span>
                    <button onClick={() => setZoom(z => Math.min(2, z + 0.1))} className="px-2 hover:text-cyan-400 transition-colors">+</button>
                </div>
            </div>

            <div ref={boardRef} className={`absolute inset-0 select-none touch-none ${isPanning ? 'cursor-grabbing' : 'cursor-grab'}`} onPointerDown={(e) => handlePointerDown(e, null)} onPointerMove={handlePointerMove} onPointerUp={handlePointerUp} onPointerLeave={handlePointerUp}>
                <div style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: '0 0', width: '100%', height: '100%' }}>
                    <div className="absolute top-[-10000px] left-[-10000px] w-[20000px] h-[20000px] opacity-20 pointer-events-none" style={{ backgroundImage: 'radial-gradient(#fff 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

                    <svg className="absolute inset-0 w-full h-full overflow-visible pointer-events-none z-0">
                        {edges.map((edge) => {
                            const source = nodes.find((n) => n.id === edge.source); 
                            const target = nodes.find((n) => n.id === edge.target);
                            if (!source || !target) return null;
                            
                            const midX = (source.x + target.x) / 2; 
                            const midY = (source.y + target.y) / 2;
                            
                            let strokeColor = "rgba(100, 116, 139, 0.6)"; 
                            if (edge.track === 'BOB') strokeColor = "rgba(234, 179, 8, 0.6)"; 
                            if (edge.track === 'TAG') strokeColor = "rgba(239, 68, 68, 0.6)"; 
                            if (edge.track === 'DR. MARSH' || edge.track === 'OPERATOR') strokeColor = "rgba(6, 182, 212, 0.6)"; 

                            return (
                                <g key={edge.id}>
                                    <line x1={source.x} y1={source.y} x2={target.x} y2={target.y} stroke={strokeColor} strokeWidth="3" strokeDasharray="4 4" />
                                    {edge.cipherCode && (
                                        <foreignObject x={midX - 40} y={midY - 40} width="80" height="80" className="pointer-events-none overflow-visible">
                                            <div 
                                                className={`flex flex-col items-center justify-center bg-[#0a0a0a] border rounded p-1 shadow-lg pointer-events-auto cursor-pointer transition-colors ${edge.cipherType === 'sigil' ? 'border-red-900/50 hover:border-red-500' : 'border-green-900/50 hover:border-green-500'}`}
                                                onDoubleClick={() => isAdmin && deleteEdge(edge.id)}
                                                title={edge.transitLog || "Vector Connection"}
                                            >
                                                {edge.cipherVisual ? (
                                                    <MiniCipher type={edge.cipherType} data={edge.cipherVisual} size={32} />
                                                ) : (
                                                    <Icons.Network size={24} className={edge.cipherType === 'sigil' ? 'text-red-500' : 'text-green-500'} />
                                                )}
                                                <div className={`text-[7px] font-bold mt-1 ${edge.cipherType === 'sigil' ? 'text-red-500' : 'text-green-500'}`}>
                                                    {edge.cipherCode}
                                                </div>
                                            </div>
                                        </foreignObject>
                                    )}
                                </g>
                            );
                        })}
                    </svg>

                    {nodes.map(node => {
                        let bgColor = 'bg-gray-900'; let borderColor = 'border-gray-700'; let textColor = 'text-gray-300';
                        if (node.type.includes('Log')) { bgColor = 'bg-yellow-950/80'; borderColor = 'border-yellow-500/50'; textColor = 'text-yellow-400'; }
                        else if (node.type === 'Lore') { bgColor = 'bg-indigo-950/80'; borderColor = 'border-indigo-500/50'; textColor = 'text-indigo-300'; }
                        else if (node.type === 'Artifact' || node.type === 'Door') { bgColor = 'bg-red-950/80'; borderColor = 'border-red-500/50'; textColor = 'text-red-300'; }

                        return (
                            <div 
                                key={node.id}
                                className={`absolute z-10 ${bgColor} border ${borderColor} rounded p-3 w-40 shadow-2xl flex flex-col gap-2 hover:border-white transition-colors cursor-grab active:cursor-grabbing backdrop-blur-md`}
                                style={{ left: node.x, top: node.y, transform: 'translate(-50%, -50%)' }}
                                onPointerDown={(e) => handlePointerDown(e, node.id)}
                                onDoubleClick={() => isAdmin && deleteNode(node.id)}
                            >
                                <div className={`text-[9px] font-bold tracking-widest uppercase ${textColor} border-b border-gray-800/50 pb-1`}>{node.type}</div>
                                <div className="text-sm font-bold text-white leading-tight break-words whitespace-normal">{node.label}</div>
                                {isAdmin && <div className="absolute -top-3 -right-3 bg-black border border-red-900 rounded-full w-6 h-6 flex items-center justify-center text-[10px] text-gray-500 opacity-0 hover:opacity-100 hover:text-red-500 hover:border-red-500 transition-all font-bold shadow-lg">X</div>}
                            </div>
                        );
                    })}
                </div>
            </div>

            <AddNodeModal isOpen={showNodeModal} onClose={() => setShowNodeModal(false)} availableNodes={availableItems} onAdd={addNode} />
            <AddEdgeModal isOpen={showEdgeModal} onClose={() => setShowEdgeModal(false)} nodes={nodes} ciphers={dbData?.ciphers || []} onAdd={addEdge} />
        </div>
    );
};

export default RabbitHoleView;